import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type UserRole = "controller" | "project_manager" | "field_supervisor" | "accounting" | "admin";

export interface UserRoleInfo {
  roleName: UserRole;
  displayName: string;
  companyId: string;
}

const ROLE_DISPLAY: Record<UserRole, string> = {
  controller: "Controller",
  project_manager: "Project Manager",
  field_supervisor: "Field Supervisor",
  accounting: "Accounting",
  admin: "Admin",
};

/**
 * Map DB role names (which vary by company) to canonical UI role keys.
 * Case-insensitive matching covers "Controller" vs "controller", etc.
 */
const DB_ROLE_MAP: Record<string, UserRole> = {
  controller: "controller",
  super_admin: "admin",
  admin: "admin",
  project_manager: "project_manager",
  site_supervisor: "field_supervisor",
  field_supervisor: "field_supervisor",
  field_manager: "field_supervisor",
  accountant: "accounting",
  accounting: "accounting",
};

export const useUserRole = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user_role", user?.id],
    queryFn: async (): Promise<UserRoleInfo | null> => {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("company_id, role_id, roles(name)")
        .eq("user_id", user!.id)
        .maybeSingle();

      if (error || !data) return null;

      const dbRoleName = ((data.roles as any)?.name ?? "controller") as string;
      const roleName = DB_ROLE_MAP[dbRoleName.toLowerCase()] ?? "controller";
      return {
        roleName,
        displayName: ROLE_DISPLAY[roleName] ?? roleName,
        companyId: data.company_id,
      };
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 30,
  });
};
